
import {
  computed, defineComponent, onBeforeMount, reactive, watch,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useGradeBase } from '@/core/composables/GradeBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { IOrdenacao } from '@/core/models/Consulta/IOrdenacao';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import Card from '@/core/components/Tela/Card.vue';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import { ETipoSelecaoProduto } from '@/models/Enumeradores/Estoques/ETipoSelecaoProduto';
import { useModalBase } from '@/core/composables/ModalBase';
import { IDTOPedidoCompra, IDTOPedidoCompraItem } from '@/models/DTO/Compras/IDTOPedidoCompra';
import ServicoPedidoCompra from '@/servicos/Compras/ServicoPedidoCompras';

export default defineComponent({
  name: 'ValidarPedidoCompraModal',
  props: {
    visivel: {
      type: Boolean,
    },
    empresa: {
      type: Number,
      required: true,
    },
    codigosSelecionados: {
      type: Array as () => number[],
      default: () => [],
    },
    pedidos: {
      type: Array as () => number[],
      default: () => [],
    },
    produtos: {
      type: Array as () => IDTOPedidoCompraItem[],
      default: () => [],
    },
  },
  components: {
    Icone,
    MensagemSemDados,
    Card,
  },
  emits: ['update:visivel', 'update:pedidos', 'update:codigosSelecionados', 'update:empresa', 'confirmacao'],
  setup(props, { emit }) {
    const { modalBase } = useModalBase(props, emit);
    const { telaBase } = useTelaBase();
    const { gradeBase, preenchePropriedadesNosFiltrosEOrdenacao, defineValoresIniciaisGrade } = useGradeBase();
    const servicoPedidoCompra = new ServicoPedidoCompra();
    servicoPedidoCompra.requisicaoSistema();

    const state = reactive({
      ordenacaoCampos: [] as IOrdenacao[],
      colunas: [] as IColumn[],
      colunasProdutos: [] as IColumn[],
      indexPedidoSelecionado: -1,
      pedidosCompra: [] as IDTOPedidoCompra[],
      windowHeight: (window.innerHeight - 300),
      windowWidth: (window.innerWidth - 100),
      codigosSelecionados: [] as number[],
    });

    const computedCodigosSelecionados = computed({
      get: () => props.codigosSelecionados,
      set: (val: number[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    const computedEmpresa = computed({
      get: () => props.empresa,
      set: (val: number) => {
        emit('update:empresa', val);
      },
    });

    function preencherColunas() {
      state.colunas = [
        {
          title: 'Código Pedido', dataIndex: 'codigo', key: 'Codigo', position: 0, visible: false, ordering: true,
        },
        {
          title: 'Numero Pedido', dataIndex: 'numeroPedido', key: 'NumeroPedido', width: 80, ellipsis: true, position: 1, visible: true, ordering: true, align: 'right',
        },
        {
          title: 'Numero Pedido Fornecedor', dataIndex: 'numeroPedidoFornecedor', key: 'NumeroPedidoFornecedor', width: 100, ellipsis: true, position: 2, visible: true, ordering: true, align: 'right',
        },
        {
          title: 'Data Criação', dataIndex: 'dataCriacao', key: 'DataCriacao', width: 70, ellipsis: true, position: 3, visible: true, ordering: true, align: 'center', customRenderRow: ECustomRenderRow.MascararData,
        },
        {
          title: 'Data Previsão Entrega', dataIndex: 'dataPrevisaoEntrega', key: 'DataPrevisaoEntrega', width: 100, ellipsis: true, position: 4, visible: true, ordering: true, align: 'center', customRenderRow: ECustomRenderRow.MascararData,
        },
        {
          title: 'Total Pedido', dataIndex: 'totalPedido', key: 'TotalPedido', width: 100, ellipsis: true, position: 4, visible: true, ordering: true, align: 'left', customRenderRow: ECustomRenderRow.MascararFracionado,
        },
      ];
    }

    function preencherColunasProdutos() {
      state.colunasProdutos = [];
      state.colunasProdutos.push({
        title: 'Codigo', dataIndex: 'codigo', key: 'codigo', position: 0, visible: false,
      });
      state.colunasProdutos.push({
        title: 'Produto', dataIndex: 'descricaoProduto', key: 'descricaoProduto', position: 1, visible: true, ordering: true, align: 'left', width: 400,
      });
      state.colunasProdutos.push({
        title: 'Unidade', dataIndex: 'siglaUnidade', key: 'siglaUnidade', position: 3, visible: true, ordering: true, align: 'center', width: 250,
      });
      state.colunasProdutos.push({
        title: 'Qtd Pedido', dataIndex: 'quantidade', key: 'quantidade', position: 4, visible: true, ordering: true, align: 'right', width: 140, customRenderRow: ECustomRenderRow.MascararFracionado,
      });
      state.colunasProdutos.push({
        title: 'Qtd Atendida', dataIndex: 'quantidadeAtendida', key: 'quantidadeAtendida', position: 5, visible: true, ordering: true, align: 'right', width: 140, customRenderRow: ECustomRenderRow.MascararFracionado,
      });
    }

    function limparTela() {
      state.ordenacaoCampos = [] as IOrdenacao[];
      state.colunas = [] as IColumn[];
      state.colunasProdutos = [] as IColumn[];
      state.indexPedidoSelecionado = -1;
      preencherColunas();
      preencherColunasProdutos();
    }

    const onChange = (selectedRowKeys: any) => {
      state.codigosSelecionados = selectedRowKeys;
      if (selectedRowKeys.length === 0) {
        state.indexPedidoSelecionado = -1;
      }
    };

    const onSelect = (record: IDTOPedidoCompra, selected: boolean) => {
    // Se o usuário acabou de MARCAR o checkbox (selected = true)
      if (selected) {
        const index = state.pedidosCompra.findIndex((p) => p.codigo === record.codigo);
        state.indexPedidoSelecionado = index;
      // eslint-disable-next-line brace-style
      }
      // Se o usuário DESMARCOU o checkbox
      else {
        // Lógica opcional: Se desmarcou o item que estava sendo visto,
        // tenta mostrar o primeiro item que sobrou selecionado (se houver)
        // eslint-disable-next-line no-lonely-if
        if (state.pedidosCompra[state.indexPedidoSelecionado]?.codigo === record.codigo) {
          if (state.codigosSelecionados.length > 0) {
            // Busca o primeiro ID que sobrou na lista de selecionados
            const idSobrou = state.codigosSelecionados[0];
            state.indexPedidoSelecionado = state.pedidosCompra.findIndex((p) => p.codigo === idSobrou);
          } else {
            state.indexPedidoSelecionado = -1;
          }
        }
      }
    };

    async function reiniciarTela() {
      limparTela();
      await preenchePropriedadesNosFiltrosEOrdenacao();
      await defineValoresIniciaisGrade();
    }

    function confirmar() {
      state.pedidosCompra.forEach((pc) => {
        for (let i = 0; i < state.codigosSelecionados.length; i += 1) {
          if (pc.codigo === state.codigosSelecionados[i]) {
            computedCodigosSelecionados.value.push(pc.numeroPedido);
          }
        }
      });
      emit('confirmacao');
      modalBase.computedVisivel = false;
    }

    const onRowClick = (record: IDTOPedidoCompra) => ({
      onClick: () => {
        const index = state.pedidosCompra.findIndex((p) => p.codigo === record.codigo);
        state.indexPedidoSelecionado = index;
      },
    });

    const onSelectPedido = (record: IDTOPedidoCompra, selected: boolean) => {
      if (selected) {
        const index = state.pedidosCompra.findIndex((p) => p.codigo === record.codigo);
        state.indexPedidoSelecionado = index;
      } else {
        state.indexPedidoSelecionado = -1;
      }
    };

    const rowClassName = (record: IDTOPedidoCompra) => {
    // Se o índice deste registro for igual ao índice selecionado para visualização, aplica a classe
      if (state.indexPedidoSelecionado > -1
        && state.pedidosCompra[state.indexPedidoSelecionado].codigo === record.codigo) {
        return 'linha-em-foco';
      }
      return '';
    };

    async function PreencherQuantidadeAtendida() {
      for (let i = 0; i < props.produtos.length; i += 1) {
        for (let j = 0; j < state.pedidosCompra.length; j += 1) {
          const pedido = state.pedidosCompra[j];
          for (let k = 0; k < pedido.itens.length; k += 1) {
            const item = pedido.itens[k];
            if (item.codigo === props.produtos[i].codigo
              && item.codigoProdutoDefinicao === props.produtos[i].codigoProdutoDefinicao) {
              state.pedidosCompra[j].itens[k].quantidadeAtendida = props.produtos[i].quantidade;
            }
          }
        }
      }
    }

    onBeforeMount(async () => {
      reiniciarTela();
    });

    watch(async () => computedEmpresa.value, async () => {
      reiniciarTela();
    });

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      limparTela();
      if (modalBase.computedVisivel) {
        preencherColunas();
        state.pedidosCompra = await servicoPedidoCompra.obterPedidosCompraRecebimento(props.empresa, props.pedidos);
        await PreencherQuantidadeAtendida();
      }
      telaBase.carregando = false;
    });

    return {
      props,
      state,
      telaBase,
      gradeBase,
      ECustomRenderRow,
      ETipoSelecaoProduto,
      UtilitarioMascara,
      UtilitarioData,
      UtilitarioGeral,
      onChange,
      computedCodigosSelecionados,
      computedEmpresa,
      modalBase,
      confirmar,
      onSelectPedido,
      onRowClick,
      rowClassName,
      onSelect,
    };
  },
});
